import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AddThinIcon from '@/assets/all-icons/add-thin-icon';
import DownloadTemplateIcon from '@/assets/all-icons/download-template-icon';
import EditArtworkIcon from '@/assets/all-icons/edit-artwork-icon';
import GalleryIcon from '@/assets/all-icons/gallery-icon';
import QrCodeIcon from '@/assets/all-icons/qr-code-icon';
import ShareIcon from '@/assets/all-icons/share-icon';
import UserIcon from '@/assets/all-icons/user-icon';
import config             from '@/config';                          // <<< nuevo
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import PopupUpdateProfilePicture from '@/pages/artwork/artwork-header-mobile/options/download-template/components/PopupUpdateProfilePicture';
import ArtworkAi from '@/pages/artwork/content-section/socials/artwork-ai';
import {
  getArtworkAiView,
  getArtworkGalleryId,
  getArtworkStatus,
  isViewerArtworkGalleryOwner,
  isViewerDisplayedArtistOwner,
} from '@/pages/artwork/utils/artworkView.selectors';
import { buildArtworkShareGatewayUrl, resolveAuctionSharePayload } from '@/pages/artwork/utils/share.utils';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import fetchWrapper       from '@/services/fetchWrapper.service';   // <<< nuevo
import ThoughtIcon from '@/shared-components/icons/components/artwork/ThoughtIcon';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericBottomSheet from '@/shared-components/popups/components/GenericBottomSheet';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';
import { handleShareFailure } from '@/utils/share.utils';

import OptionItem from './components/ArtworkViewOptions';
import SaveArtwork from './components/SaveArtwork';
import AddToArtlist from './add-to-artlist';
import DowloadTemplate from './download-template';
import QrPreview from './qr-code';

import './index.css';

export default function Options({ open, close, artworkData: artworkDataProp, anchorRef }) {
  const t = useTranslations('common.shared.artwork_view.options');
  const editBlockedEmail = 'galleries@suarte.art';
  const context = useContext(ArtworkDataContext);
  const artworkData = artworkDataProp || context?.artworkData;
  const artworkMainImageId = artworkData?.artwork_media?.artwork_main_picture?.image_id;
  const { state, stateHandler } = useStateHandler();
  const { pathname } = useLocation();
  const [openAddToArtlist, setOpenAddToArtlist] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDownloadTemplate, setOpenDownloadTemplate] = useState(false);
  const [openQrPreview, setOpenQrPreview] = useState(false);
  const [openCropPopup, setOpenCropPopup] = useState(false); // State for crop popup
  const [openCoverCrop, setOpenCoverCrop] = useState(false);
  const [openAskAi, setOpenAskAi] = useState(false);
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });
  const navigate = useNavigate();
  const [permsDoc, setPermsDoc] = useState(null);  
  const optionsRef = useRef(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const galleryId = state?.user_session?.gallery_id || getArtworkGalleryId(artworkData);
  const isGalleryMember = Boolean(state?.user_session?.gallery_member);
  const interactionSlug =
    artworkData?.slug
    ?? artworkData?.artwork_about?.artwork_slug
    ?? artworkData?.artwork_slug
    ?? null;

  function shareArtwork() {
    if (!artworkData) return;
    if (interactionSlug) {
      trackArtworkPreferenceInteraction({
        slug: interactionSlug,
        signal: ARTWORK_PREFERENCE_SIGNALS.SHARE,
        metadata: { source: 'artwork-options__artwork-option' },
      });
    }
    const artworkTitle = artworkData?.artwork_about?.artwork_title || 'Artwork';
    const sharerUsername = state?.user_session?.user_username
      || state?.user_session?.username
      || null;
    const {
      shareTitle,
      shareDescription,
      shareUrl,
    } = resolveAuctionSharePayload({ artworkData, sharerUsername });
    const shareGatewayUrl = buildArtworkShareGatewayUrl({
      artworkData,
      auctionData: artworkData?.artwork_auction,
      apiBaseUrl: config.app.shareGatewayBaseUrl || config.apis.api.url,
      sharerUsername,
    });

    const sharePayload = {
      title: shareTitle || artworkTitle,
      text: shareDescription || t('share_payload.text', { title: artworkTitle }),
      url: shareGatewayUrl || shareUrl,
    };
    console.debug('[ArtworkShare][mobile-options] payload', sharePayload);

    navigator
      .share(sharePayload)
      .catch((error) => handleShareFailure(error, stateHandler, 'Artwork share'));
  }

  function handleEditArtwork() {
    if (!artworkData) return;
    if (artworkData?.artwork_auction?.is_enabled) {
      return;
    }
    const artworkStatus = getArtworkStatus(artworkData);
    if (artworkStatus === "original_sold" || artworkStatus === "sold") {
      setOpenEditDialog(true);
    } else {
      navigate("/profile/edit-artwork", { state: { from: true, artworkData, permissions: permsDoc?.permissions } });
    }
  }

  function isArtworkGallery() {
    return isViewerArtworkGalleryOwner(artworkData, state.user_session);
  }
  function isArtistOwner() {
    return isViewerDisplayedArtistOwner(artworkData, state.user_session);
  }

  useEffect(() => {
    if (!artworkData) {
      return;
    }
    (async () => {
    // 1) sólo si es Work of Artist y el usuario es su artista
      if (
        !isArtistOwner() ||
      artworkData.artwork_about.artwork_type !== 'Work of Artist'
      ) {
        return;
      }

      /* ── 2) obtenemos los IDs ───────────────────── */
      // gallery_artist puede venir como string o como objeto {_id: …}
      const artistField = state.user_session?.gallery_artist;
      const artistId =
      typeof artistField === 'string'
        ? artistField
        : artistField?._id;          // null si no existe

      const galleryId = getArtworkGalleryId(artworkData);

      if (!artistId || !galleryId) return;   // seguridad

      /* ── 3) llamamos al nuevo endpoint ──────────── */
      try {
        const { response, data } = await fetchWrapper.get(
          `${config.apis.api.url}/permissions/check?artist=${artistId}&gallery=${galleryId}`,
          { injectToken: true }
        );

        if (response.ok) {
        // data = { permissions: {...} | null, canEdit: Boolean }
          if (data.permissions) setPermsDoc(data.permissions);
        }
      } catch (err) {
        console.error('Error fetching permissions', err);
      }
    })();
  }, [artworkData]);       // ← dependencias vacías: se ejecuta 1 vez

  /* ─── artista puede editar si hay flags true ─── */       // <<< nuevo
  const artistCanEdit = () =>
    permsDoc && permsDoc.permissions &&
    Object.values(permsDoc.permissions).some(Boolean);
  const galleryMemberCanEdit = () => {
    if (!isGalleryMember) return false;
    const permissionKeys = [
      'artworks.edit_title',
      'artworks.edit_artist',
      'artworks.edit_images',
      'artworks.edit_secondary_images',
      'artworks.edit_audio',
      'artworks.edit_type',
      'artworks.edit_medium',
      'artworks.edit_description',
      'artworks.edit_themes',
      'artworks.edit_feelings',
      'artworks.edit_colors',
      'artworks.edit_shipping',
      'artworks.edit_frame',
      'artworks.edit_certificate',
      'artworks.edit_size',
      'artworks.edit_price',
      'artworks.edit_year',
      'artworks.edit_schedule',
      'artworks.request_limited_editions',
    ];
    return permissionKeys.some((permissionKey) =>
      canAccessGalleryPermission(state?.user_session, permissionKey, galleryId),
    );
  };
  const galleryMemberCanManageQr = () =>
    isGalleryMember && canAccessGalleryPermission(state?.user_session, 'artworks.inventory', galleryId);

  const isAuctionEnabled = Boolean(artworkData?.artwork_auction?.is_enabled);
  const showEditArtwork =
    !isAuctionEnabled &&
    (
      (isArtworkGallery() && artworkData?.artwork_status !== 'unavailable') ||
      (isArtistOwner() &&
        artworkData?.artwork_about?.artwork_type === 'Work of Artist' && artworkData?.artwork_status !== 'unavailable' &&
        artistCanEdit())
      ||
      (galleryMemberCanEdit() && artworkData?.artwork_status !== 'unavailable')
    );
  const showAskAiOption = useMemo(() =>
  {
    return getArtworkAiView(artworkData).canOpenAskAi;
  }, [artworkData]);
  const showQrOption = isArtworkGallery() || galleryMemberCanManageQr();

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleOutsideClick(event) {
      if (!optionsRef.current || optionsRef.current.contains(event.target)) {
        return;
      }

      if (typeof close === 'function') {
        close();
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [open, close]);

  // New function to handle setting the artwork as the profile picture
  function handleSetAsProfilePicture() {
    setOpenCropPopup(true); // Open the crop popup for profile picture selection
  }

  function handleSetAsCoverBackground() {
    setOpenCoverCrop(true);
  }

  function handleOpenAskAi()
  {
    if (typeof close === "function")
    {
      close();
    }
    setOpenAskAi(true);
  }

  function handleOpenQrPreview()
  {
    if (typeof close === "function")
    {
      close();
    }
    privateActionHandler(() => setOpenQrPreview(true));
  }

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setAnchorPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchorElement = anchorRef.current.querySelector('.artwork-view-top-header__options-button') || anchorRef.current;
      if (!anchorElement) {
        setAnchorPosition(null);
        return;
      }
      const rect = anchorElement.getBoundingClientRect();
      setAnchorPosition({
        top: rect.top,
        right: Math.max(0, window.innerWidth - rect.right)
      });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef]);

  const anchoredStyle = anchorPosition
    ? { top: `${anchorPosition.top}px`, right: `${anchorPosition.right}px` }
    : undefined;

  const containerClassName = anchorPosition
    ? 'artwork-view-options-container artwork-view-options-container--anchored'
    : 'artwork-view-options-container';

  return (
    <>
      <GenericPopup
        open={open}
        className="artwork-options__container"
        animation={false}
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
        onClick={close}
      >
        <div className="artwork-options__background" />

        <div className="artwork-options__wrapper element-non-selectable">
          <div
            className={containerClassName}
            style={anchoredStyle}
          >
            <ThreeDotsHorizontal className={pathname.includes("suarteworld") ? "artwork-options__fake-dots suarteworld" : "artwork-options__fake-dots"} />

            <div className="artwork-options__options" ref={optionsRef}>

              {showEditArtwork && (
                <>
                  <OptionItem
                    text={t('menu.edit_artwork')}
                    icon={<EditArtworkIcon className="edit-artwork-icon" />}
                    onClick={handleEditArtwork}
                  />

                  {isArtworkGallery() && artworkMainImageId && (
                    <OptionItem
                      text={t('menu.template')}
                      icon={<DownloadTemplateIcon className="download-template-icon" />}
                      onClick={() =>
                        privateActionHandler(() => setOpenDownloadTemplate(true))
                      }
                    />
                  )}
                </>
              )}

              {showQrOption && (
                <OptionItem
                  text={t('menu.qr_code')}
                  icon={<QrCodeIcon className="qr-icon" />}
                  onClick={handleOpenQrPreview}
                />
              )}

              <OptionItem
                text={t('menu.add_to_artlist')}
                icon={<AddThinIcon className="add-to-artlist-icon" />}
                onClick={() => {
                  if (interactionSlug) {
                    trackArtworkPreferenceInteraction({
                      slug: interactionSlug,
                      signal: ARTWORK_PREFERENCE_SIGNALS.ADD_TO_ARTLIST,
                      metadata: { source: 'add-to-artlist-icon__outline' },
                    });
                  }
                  privateActionHandler(() => setOpenAddToArtlist(true));
                }}
              />

              <SaveArtwork
                artworkData={artworkData}
                socialsData={context?.socialsData}
                setSocialsData={context?.setSocialsData}
                onCompleted={close}
              />

              {showAskAiOption && (
                <OptionItem
                  text={t('menu.ask_ai')}
                  icon={<ThoughtIcon className="ask-ai-icon" />}
                  onClick={handleOpenAskAi}
                />
              )}
              
              {/* New "Set as profile picture" option */}
              <OptionItem
                text={t('menu.set_as_profile_picture')}
                icon={<UserIcon className="user-icon" />} // Use an appropriate icon
                onClick={handleSetAsProfilePicture}
              />

              {(isArtworkGallery() || isArtistOwner()) && (
                <OptionItem
                  text={t('menu.set_as_cover_background')}
                  icon={<GalleryIcon className="user-icon" />}
                  onClick={handleSetAsCoverBackground}
                />
              )}

              {navigator.share && (
                <OptionItem
                  text={t('menu.share')}
                  icon={<ShareIcon className="share-icon" />}
                  onClick={shareArtwork}
                />
              )}
            </div>
          </div>
        </div>
      </GenericPopup>

      <GenericBottomSheet
        className="artwork-view-socials-bottom-sheet"
        open={openAskAi}
        close={() => setOpenAskAi(false)}
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
      >
        <div className="artwork-view-ai__sheet-content">
          <ArtworkAi closeSocial={() => setOpenAskAi(false)} />
        </div>
      </GenericBottomSheet>

      <AddToArtlist open={openAddToArtlist} close={() => setOpenAddToArtlist(false)} />
      <DowloadTemplate
        open={openDownloadTemplate}
        close={() => setOpenDownloadTemplate(false)}
        artworkData={artworkData}
      />
      <QrPreview
        open={openQrPreview}
        close={() => setOpenQrPreview(false)}
        artworkData={artworkData}
      />

      {/* Crop popup for setting profile picture */}
      <PopupUpdateProfilePicture
        open={openCropPopup}
        image={artworkMainImageId ? `${config.app.imageServiceDomain}/${artworkMainImageId}/public` : ''}
        exitCrop={() => setOpenCropPopup(false)}
      />
      <PopupUpdateProfilePicture
        mode="cover"
        open={openCoverCrop}
        image={artworkMainImageId ? `${config.app.imageServiceDomain}/${artworkMainImageId}/public` : ''}
        exitCrop={() => setOpenCoverCrop(false)}
        aspect={3 / 2}
        continueLabel={t('cover_crop.continue')}
        title={t('cover_crop.title')}
        instructions={t('cover_crop.instructions')}
      />

      <GenericPopup
        open={openEditDialog}
        className="artwork-options__edit-dialog"
        opacity
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
      >
        <div className="generic-popup-header">
          <span className="generic-popup-header-text">
            {t('edit_blocked.title')}
          </span>

          <XIcon className="generic-popup-close" onClick={() => setOpenEditDialog(false)} />
        </div>

        <p className="artwork-options__edit-dialog-text font-size-medium">
          {t('edit_blocked.body_prefix')}
          {" "}
          <a href={`mailto:${editBlockedEmail}`}>{editBlockedEmail}</a>
          {" "}
          {t('edit_blocked.body_middle')}
          {t('edit_blocked.body_suffix')}
        </p>
      </GenericPopup>
    </>
  );

}
